import {
  digestBrowserUploadBody,
  type BrowserUploadIdentity,
} from './browser-upload-digest';

export type { BrowserUploadIdentity } from './browser-upload-digest';

let worker: Worker | null | undefined;
let nextId = 0;
const waiting = new Map<
  number,
  {
    resolve: (identity: BrowserUploadIdentity) => void;
    reject: (error: Error) => void;
  }
>();

function hashWorker(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker =
      typeof Worker === 'undefined'
        ? null
        : new Worker(
            new URL('./browser-upload-hash.worker.ts', import.meta.url),
            {
              type: 'module',
            }
          );
  } catch {
    worker = null;
  }
  if (worker) {
    worker.onmessage = (
      event: MessageEvent<{
        id: number;
        identity?: BrowserUploadIdentity;
        error?: string;
      }>
    ) => {
      const pending = waiting.get(event.data.id);
      if (!pending) return;
      waiting.delete(event.data.id);
      if (event.data.identity) pending.resolve(event.data.identity);
      else pending.reject(new Error(event.data.error ?? 'UPLOAD_HASH_FAILED'));
    };
    worker.onerror = () => {
      // A worker that cannot load (CSP, old browser) hashes on this thread.
      worker?.terminate();
      worker = null;
      const stranded = [...waiting.values()];
      waiting.clear();
      for (const pending of stranded)
        pending.reject(new Error('UPLOAD_HASH_WORKER_UNAVAILABLE'));
    };
  }
  return worker;
}

/**
 * The upload identity `begin` pins, computed in a worker so a large file
 * never blocks the composer. Falls back to this thread when workers fail.
 */
export async function hashBrowserUploadBody(
  body: Blob
): Promise<BrowserUploadIdentity> {
  const target = hashWorker();
  if (!target) return digestBrowserUploadBody(body);
  const id = (nextId += 1);
  try {
    return await new Promise<BrowserUploadIdentity>((resolve, reject) => {
      waiting.set(id, { resolve, reject });
      target.postMessage({ id, body });
    });
  } catch {
    return digestBrowserUploadBody(body);
  }
}
